<template>
  <div
    class="grid-cols-[minmax(0,1fr)_max-content_max-content_max-content_max-content]"
    :class="table()"
  >
    <div :class="tableHeader()">
      <div>{{ t('description') }}</div>
      <div>{{ t('dueDate') }}</div>
      <div :class="tableCell({ numeric: true })">{{ t('amount') }}</div>
      <div :class="tableCell({ numeric: true })">{{ t('open') }}</div>
      <div :class="tableCell({ numeric: true })">{{ t('allocated') }}</div>
    </div>
    <template v-if="allocationItems?.length">
      <template v-for="allocation in allocationItems" :key="allocation.id">
        <div
          :class="
            tableRow({
              interactive: hasAllocations(allocation.ledgerEntry),
            })
          "
          :role="hasAllocations(allocation.ledgerEntry) ? 'button' : undefined"
          :tabindex="hasAllocations(allocation.ledgerEntry) ? 0 : -1"
          :aria-expanded="
            hasAllocations(allocation.ledgerEntry)
              ? !!expandedAllocations[allocation.id]
              : undefined
          "
          data-testid="ledger-entry-allocation-item"
          @click="toggleExpanded(allocation.id, allocation.ledgerEntry)"
          @keydown.enter.prevent="toggleExpanded(allocation.id, allocation.ledgerEntry)"
          @keydown.space.prevent="toggleExpanded(allocation.id, allocation.ledgerEntry)"
        >
          <div class="flex min-w-0 items-center gap-2">
            <div class="inline-flex size-5 items-center justify-center rounded-sm">
              <ChevronRightIcon
                class="size-4 text-obsidian-600 transition-transform"
                :class="{
                  'rotate-90': expandedAllocations[allocation.id],
                  'opacity-30': !hasAllocations(allocation.ledgerEntry),
                }"
              />
            </div>
            <LedgerEntryListItemTitle :entry="allocation.ledgerEntry">
              <template v-if="bookingOf(allocation.ledgerEntry)" #beforeLane>
                <router-link
                  :to="{
                    name: 'bookingEdit',
                    params: { id: bookingOf(allocation.ledgerEntry)!.id },
                  }"
                  class="text-link"
                  @click.stop
                >
                  {{
                    t('bookingNumber', { number: bookingOf(allocation.ledgerEntry)!.bookingNumber })
                  }}
                </router-link>
              </template>
            </LedgerEntryListItemTitle>
          </div>
          <div>
            {{
              allocation.ledgerEntry.dueDate
                ? formatDate(allocation.ledgerEntry.dueDate, dateTimeFormat)
                : '-'
            }}
          </div>
          <div :class="tableCell({ class: getAmountTextColor(allocation.ledgerEntry.amount) })">
            {{ allocation.ledgerEntry.amount > 0 ? '+' : ''
            }}{{ formatCurrency(allocation.ledgerEntry.amount / 100) }}
          </div>
          <div
            :class="
              tableCell({
                numeric: true,
                class: getAmountTextColor(allocation.ledgerEntry[amountToAllocateKey]),
              })
            "
          >
            <span v-currency="allocation.ledgerEntry[amountToAllocateKey] / 100" />
          </div>
          <div
            :class="
              tableCell({
                numeric: true,
                class: [getAmountTextColor(allocation.amount), 'font-bold'],
              })
            "
          >
            {{ formatCurrency(allocation.amount / 100) }}
          </div>
        </div>
        <div
          v-if="expandedAllocations[allocation.id]"
          class="col-span-full overflow-x-hidden border-b border-black/5 bg-obsidian-50 px-4 py-3"
        >
          <LedgerEntryAllocationDetails :entry="allocation.ledgerEntry" />
        </div>
      </template>
      <div :class="tableFooter()" data-testid="ledger-entry-allocation-total">
        <div class="col-span-4 flex items-center">{{ t('total') }}</div>
        <div :class="[tableCell({ numeric: true }), 'font-bold']">
          {{
            formatCurrency(sumBy((allocationItems ?? []) as { amount: number }[], 'amount') / 100)
          }}
        </div>
      </div>
    </template>
    <EntityListExceptions
      :status="status"
      :error="error"
      :items-count="allocationItems?.length ?? 0"
      :no-items-text="t('noAllocations')"
      class="col-span-full"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatCurrency } from '@/utils/format-numbers.ts';
import { sumBy } from 'lodash';
import { useInvoiceAllocations, usePaymentAllocations } from '@/queries/use-ledger.ts';
import type { LedgerEntry } from '@/entities/ledger/ledger-entry.entity.ts';
import EntityListExceptions from '../EntityListExceptions.vue';
import { formatDate } from '@/hooks/use-formatted-date.ts';
import { useDateTimeFormat } from '@/hooks/use-date-format.ts';
import LedgerEntryListItemTitle from './LedgerEntryListItemTitle.vue';
import LedgerEntryAllocationDetails from './LedgerEntryAllocationDetails.vue';
import {
  table,
  tableCell,
  tableFooter,
  tableHeader,
  tableRow,
} from '../../../../theme/lib/cva/grid.ts';
import { getAmountTextColor } from './utils/get-amount-text-color.ts';
import { ChevronRightIcon } from 'lucide-vue-next';

const props = defineProps<
  (
    | {
        paymentId: string;
        invoiceId?: never;
      }
    | {
        invoiceId: string;
        paymentId?: never;
      }
  ) & {
    /** Booking the list is shown for; entries of this booking get no link to it. */
    bookingId?: string | null;
  }
>();

const dateTimeFormat = useDateTimeFormat();

const { t } = useI18n();

const amountToAllocateKey = computed(() => {
  return 'paymentId' in props && props.paymentId ? 'amountToPay' : 'amountToInvoice';
});

const paymentAllocations = usePaymentAllocations(
  computed(() => ('paymentId' in props && props.paymentId) || undefined),
);
const invoiceAllocations = useInvoiceAllocations(
  computed(() => ('invoiceId' in props && props.invoiceId) || undefined),
);

const allocations = computed(() => {
  return 'paymentId' in props && props.paymentId ? paymentAllocations : invoiceAllocations;
});

const allocationItems = computed(() => {
  return allocations.value.data.value;
});
const status = computed(() => allocations.value.status.value);
const error = computed(() => allocations.value.error.value);
const expandedAllocations = ref<Record<string, boolean>>({});

const hasAllocations = (entry: LedgerEntry) => {
  return !!(
    entry.invoiceAllocations?.length ||
    entry.paymentAllocations?.length ||
    entry.nettingAllocations?.length
  );
};

const bookingOf = (entry: LedgerEntry) => {
  const booking = (
    entry as LedgerEntry & {
      booking?: { id: string; bookingNumber?: number | null } | null;
    }
  ).booking;
  return booking?.id && booking.id !== props.bookingId ? booking : undefined;
};

const toggleExpanded = (allocationId: string, entry: LedgerEntry) => {
  if (!hasAllocations(entry)) return;
  expandedAllocations.value = {
    ...expandedAllocations.value,
    [allocationId]: !expandedAllocations.value[allocationId],
  };
};
</script>

<i18n lang="json">
{
  "en": {
    "description": "Description",
    "dueDate": "Due Date",
    "allocated": "Allocated",
    "open": "Open",
    "amount": "Amount",
    "total": "Total",
    "noAllocations": "No allocations found",
    "bookingNumber": "Booking {number}"
  },
  "de": {
    "description": "Beschreibung",
    "dueDate": "Fälligkeitsdatum",
    "allocated": "Zugeordnet",
    "open": "Offen",
    "amount": "Betrag",
    "total": "Gesamt",
    "noAllocations": "Keine Zuordnungen gefunden",
    "bookingNumber": "Buchung {number}"
  },
  "es": {
    "description": "Descripción",
    "dueDate": "Fecha de vencimiento",
    "allocated": "Imputado",
    "open": "Pendiente",
    "amount": "Importe",
    "total": "Total",
    "noAllocations": "No se encontraron imputaciones",
    "bookingNumber": "Reserva {number}"
  }
}
</i18n>
